import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  InputDatePicker,
  ModalConfirm,
  NumberInput,
  RemoveButton,
  Select,
  TextField,
} from "@src/components/ui-kit";
import { currency } from "@src/utils/formatters";
import { fieldNumberModelToInputProps } from "@src/utils/forms/inputHelpers";
import { useStore as useAppStore } from "@src/utils/hooks";
import { ContractorSelect } from "@src/widgets/ContractorSelect";
import { observer } from "mobx-react-lite";
import { useStore } from "../Store";
import type { PlannedExpensesAmountMode } from "./store";

export const PlannedExpensesModal = observer(function PlannedExpensesModal() {
  const projectStore = useStore();
  const { workspaceStore } = useAppStore();
  const store = projectStore.plannedExpensesModalStore;

  return (
    <>
      <Modal
        isOpen={store.modalState.isOpen}
        onClose={store.modalState.onClose}
        scrollBehavior="inside"
        size="5xl"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent maxW="1200px">
          <ModalHeader>
            <HStack align="center" justify="space-between" pr="10">
              <VStack align="start" spacing="0">
                <Text>
                  <Trans>Planned expenses</Trans>
                </Text>
                <Text color="gray.500" fontSize="sm" fontWeight="normal">
                  {store.selectedItem?.title}
                </Text>
              </VStack>
              <Box w="180px">
                <Select
                  value={store.amountMode}
                  onChange={(value) =>
                    store.setAmountMode(value as PlannedExpensesAmountMode)
                  }
                  options={store.amountModeOptions}
                />
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {store.isLoading ? (
              <VStack py="10">
                <Spinner />
              </VStack>
            ) : (
              <VStack align="stretch" spacing="4">
                <Box>
                  <Table minW="980px" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>
                          <Trans>Item / Service</Trans>
                        </Th>
                        <Th>
                          <Trans>Contractor</Trans>
                        </Th>
                        <Th>
                          <Trans>Date</Trans>
                        </Th>
                        <Th>
                          <Trans>Client price</Trans>
                        </Th>
                        <Th>
                          <Trans>Buy price</Trans>
                        </Th>
                        <Th w="52px" />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {store.rows.map((row) => (
                        <Tr key={row.key}>
                          <Td px="0" pr="3">
                            <TextField
                              placeholder={t`Item / Service`}
                              value={row.$.description.value}
                              onChange={row.$.description.onChange}
                            />
                          </Td>
                          <Td px="0" pr="3">
                            <ContractorSelect
                              asPortal
                              defaultName={row.contractorName}
                              field={row.$.contractorId}
                              waitForDefaultName={false}
                            />
                          </Td>
                          <Td px="0" pr="3">
                            <InputDatePicker
                              onChange={(value) => {
                                row.$.date.onChange(value?.start);
                              }}
                              placeholder={t`Date`}
                              selected={row.$.date.value}
                            />
                          </Td>
                          <Td px="0" pr="3">
                            <NumberInput
                              currency
                              onBlur={row.handleClientPriceBlur}
                              suffix={workspaceStore.settings?.currency.code}
                              {...fieldNumberModelToInputProps(
                                row.$.clientPrice,
                              )}
                            />
                          </Td>
                          <Td px="0" pr="3">
                            <NumberInput
                              currency
                              suffix={workspaceStore.settings?.currency.code}
                              {...fieldNumberModelToInputProps(row.$.buyPrice)}
                            />
                          </Td>
                          <Td px="0">
                            <RemoveButton
                              onClick={() => store.removeRow(row.key)}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                <HStack justify="space-between" pt="2">
                  <Button onClick={() => store.addRow()} variant="outline">
                    <Trans>+ New item</Trans>
                  </Button>
                  <VStack align="end" spacing="0">
                    <Text color="gray.500" fontSize="sm">
                      <Trans>Total</Trans>:{" "}
                      {currency.formatByWorkspace(store.totalAmount)}
                    </Text>
                    {store.hasRemainingAmount && (
                      <Text color="gray.500" fontSize="sm">
                        <Trans>Remaining</Trans>:{" "}
                        {currency.formatByWorkspace(store.remainingAmount ?? 0)}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button
              isDisabled={!store.canApplyToAll}
              onClick={store.applyToAllDisclosure.onOpen}
              variant="outline"
            >
              <Trans>Apply to all budget items</Trans>
            </Button>
            <HStack spacing="2">
              <Button
                colorScheme="grey"
                isDisabled={store.isSaving}
                onClick={store.modalState.onClose}
                variant="ghost"
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                isDisabled={!store.canSave}
                isLoading={store.isSaving}
                onClick={store.save}
              >
                <Trans>Save</Trans>
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ModalConfirm
        confirmBtnTitle={t`Apply`}
        heading={t`Apply to all budget items`}
        isOpen={store.applyToAllDisclosure.isOpen}
        isLoading={store.isSaving}
        onClose={store.applyToAllDisclosure.onClose}
        onConfirm={store.applyToAll}
      >
        <Trans>
          Current planned expenses will overwrite planned expenses for all
          budget items in this section. Continue?
        </Trans>
      </ModalConfirm>
    </>
  );
});
